import { useEffect, useState, type RefObject } from 'react';

export interface ScrollSpyOptions {
  /** Section ids, in document order. */
  readonly sectionIds: readonly string[];

  /**
   * The scrolling pane above the split breakpoint, or null when the document scrolls.
   */
  readonly paneRef: RefObject<HTMLElement | null>;

  readonly isSplit: boolean;
}

/**
 * Returns the id of the section currently in view.
 *
 * Measures against the SCROLLING PANE, not the window, whenever the split layout is
 * active. Using `window.innerHeight` and viewport-relative tops there produces a nav
 * that never updates, because the document itself is not scrolling.
 */
export function useScrollSpy({ sectionIds, paneRef, isSplit }: ScrollSpyOptions): string {
  const [activeId, setActiveId] = useState(sectionIds[0] ?? '');

  useEffect(() => {
    const pane = paneRef.current;
    const scroller: HTMLElement | Window = isSplit && pane ? pane : window;

    let frame = 0;

    const update = () => {
      frame = 0;

      // Origin and height come from the pane when it is the scroller, so the "is this
      // section in view" question is asked in the right coordinate space.
      const originTop = isSplit && pane ? pane.getBoundingClientRect().top : 0;
      const viewportHeight = isSplit && pane ? pane.clientHeight : window.innerHeight;
      const threshold = viewportHeight * 0.35;

      let current = sectionIds[0] ?? '';

      sectionIds.forEach((id) => {
        const element = document.getElementById(id);

        if (element && element.getBoundingClientRect().top - originTop < threshold) {
          current = id;
        }
      });

      setActiveId(current);
    };

    const onScroll = () => {
      // Coalesced into a frame: a bare scroll handler doing layout reads stutters on a
      // mid-range Android phone.
      if (frame === 0) {
        frame = requestAnimationFrame(update);
      }
    };

    update();

    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      if (frame !== 0) {
        cancelAnimationFrame(frame);
      }

      scroller.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [sectionIds, paneRef, isSplit]);

  return activeId;
}
